import React, { FC, useMemo, useState } from 'react';

import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Badge from 'components/common/ui-kit/Badge';
import terms, { isRussianLocale } from 'i18n';

import useAppDispatch from '../../../hooks/common/useAppDispatch';
import useModeratorDirectionElementForm from '../../../hooks/moderator/applications/useModeratorDirectionElementForm';
import {
  ApplicationByStaff,
  CompetitiveGroupStatus,
} from '../../../models/applications/applications';
import { ChosenDirectionWithPriority } from '../../../models/applications/directions';
import {
  ApplicationEducationLevel,
  ApplicationState,
  DirectionApplicationEducationLevel,
  Trajectory,
} from '../../../models/applications/enum';
import { SelectOption, translate } from '../../../models/common';
import { createToast } from '../../../redux/actions';
import { useGetDesiredDirectionsQuery } from '../../../redux/api/applications/applications-api';
import {
  useGetAvailablePrivilegesQuery,
  useSetPrivilegeToDirectionMutation,
} from '../../../redux/api/applications/directions-api';
import { selectCurrentApplication } from '../../../redux/selectors/staff/applications';
import { formatDateTimeToRuFormat } from '../../../utils/date';
import { createInputV2Props } from '../../../utils/form-helpers';
import Button from '../../common/ui-kit/Button';
import Checkbox from '../../common/ui-kit/Checkbox';
import Input from '../../common/ui-kit/Input';
import ReactSelect from '../../common/ui-kit/Select';

const PREPARATORY_EDUCATION_LEVEL_ID = 4;

type LocalPrivilegeFormTypes = {
  usePrivilege: boolean;
  privilegeId: SelectOption;
};

const LocalPrivilegeForm = ({
  asBenefit,
  asQuota,
  asMaster,
  withoutEntranceTests,
  applicationDirectionId,
  selectedPrivilegeId,
  applicationId,
}: {
  asBenefit: boolean;
  asQuota: boolean;
  asMaster: boolean;
  withoutEntranceTests: boolean;
  applicationDirectionId: number;
  selectedPrivilegeId?: number;
  applicationId: number;
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { control, handleSubmit, setValue, watch } =
    useForm<LocalPrivilegeFormTypes>({
      defaultValues: {
        usePrivilege: !!selectedPrivilegeId,
      },
    });

  const [setPrivilegeToDirection] = useSetPrivilegeToDirectionMutation();

  const { data: privileges } = useGetAvailablePrivilegesQuery(
    applicationDirectionId,
    { skip: !applicationId || !applicationDirectionId }
  );

  const { privilegeOptions, selectedPrivilege } = useMemo(() => {
    let selectedPrivilege;
    const privilegeOptions =
      privileges && privileges.length > 0
        ? privileges.map?.(el => {
            if (el.id === selectedPrivilegeId)
              selectedPrivilege = {
                id: el.id,
                value: el.id,
                label: el.privilegeTypeResponse.title,
              };
            return {
              id: el.id,
              value: el.id,
              label: el.privilegeTypeResponse.title,
            };
          })
        : undefined;
    return { privilegeOptions, selectedPrivilege };
  }, [privileges]);

  const handleFormSubmit = handleSubmit(data => {
    setPrivilegeToDirection({
      applicationDirectionId,
      privilegeId: data.privilegeId?.value,
    })
      .unwrap()
      .then(direction => {
        setValue('privilegeId', {
          value: direction.selectedPrivilege?.id,
          label: direction.selectedPrivilege?.privilegeTypeResponse.title,
        });
        dispatch(createToast('Информация сохранена', 'success'));
      })
      .catch(() => {
        // dispatch(createToast('Во время сохранения произошла ошибка', 'danger')); TODO: ENABLE IT BACK WHEN API IS FIXED
      });
  });

  const formTitle = useMemo(() => {
    if (asMaster) {
      return 'Поступает на основании Конкурса портфолио / Олимпиады "Я-профессионал" / Всероссийского инженерного конкурса';
    }
    if (asQuota) {
      return 'Поступает на основании отдельной квоты';
    }
    if (asBenefit) {
      return 'Поступает на основании льготы';
    }
    if (withoutEntranceTests) {
      return 'Поступает без вступительных испытаний';
    }
  }, [asQuota, asBenefit, asMaster, withoutEntranceTests]);

  const usePrivilege = watch('usePrivilege');

  // Логика следующая: в заявлениях магистров можно выбрать привилегию
  // (конкурс портфолио или инженерный конкурс), т.к. в ССПВО они являются "Прочим ИД"
  // и не могут быть привязаны к КГ, а на стороне ЭПК они являются особым правом
  // и привязываются к конкретной КГ. Следовательно, всем, кроме магистров,
  // запрещаем редактировать selectedPrivilege.
  return (
    <>
      {privileges && privileges.length > 0 && (
        <form onSubmit={handleFormSubmit}>
          <Controller
            name="usePrivilege"
            control={control}
            render={({ field }) => (
              <Checkbox
                text={formTitle}
                checked={field.value}
                onChange={field.onChange}
                disabled={!asMaster}
              />
            )}
          />
          <div className="profile-form__fields">
            <Controller
              name="privilegeId"
              rules={{
                required: usePrivilege,
              }}
              control={control}
              render={({ field }) => (
                <ReactSelect
                  placeholder={'Выберите отличительный признак'}
                  className="profile-form__field"
                  markRequired={true}
                  {...field}
                  options={privilegeOptions ?? []}
                  value={field.value ?? selectedPrivilege}
                  isDisabled={!usePrivilege}
                />
              )}
            />

            {asMaster && (
              <Button
                type="submit"
                theme="success"
                style={{ alignSelf: 'center' }}
              >
                {terms.SAVE}
              </Button>
            )}
          </div>
        </form>
      )}
    </>
  );
};

type ModeratorDirectionsElementProps = {
  readonly direction: ChosenDirectionWithPriority;
};

const ModeratorDirectionsElement: FC<ModeratorDirectionsElementProps> = ({
  direction: {
    direction: {
      code,
      title,
      entranceTestsSets,
      paymentForm,
      educationLevel,
      educationForm,
      educationProgram,
      faculty,
      asBenefit,
      asQuota,
      applicationEducationLevel,
      budgetLevel,
      targetOrganizations,
      educationLanguage,
    },
    priority,
    createdAt,
    deletedAt,
    refuseEnrollmentDate,
    id: applicationDirectionId,
    profileSubjectScore,
    rtTargetAgreeStatus,
    rtTargetAgreeCreatedAt,
    selectedPrivilege,
    admitted,
    entranceTestsSatisfied,
    privilegeAdmitted,
    targetContractPresent,
    epguStatus,
  },
}) => {
  const [open, setOpen] = useState(false);

  const currentApplication: ApplicationByStaff | undefined = useSelector(
    selectCurrentApplication
  );

  const {
    fields,
    control,
    formState: { errors },
  } = useModeratorDirectionElementForm(
    applicationDirectionId,
    profileSubjectScore
  );

  const inputV2Props = createInputV2Props(errors, false);

  const { data: allegedEducationTrack } = useGetDesiredDirectionsQuery(
    {
      applicationId: currentApplication?.id ?? 0,
    },
    { skip: educationLevel.id !== PREPARATORY_EDUCATION_LEVEL_ID }
  );

  const asMaster = useMemo(
    () =>
      currentApplication?.educationLevel === ApplicationEducationLevel.MASTER,
    [currentApplication]
  );

  const eduProgramCode =
    educationProgram && educationProgram.code?.trim() !== ''
      ? `${educationProgram.code}`
      : '';

  const formattedEduProgramCode =
    eduProgramCode !== '' ? `_${eduProgramCode}` : '';

  const directionCode = educationProgram
    ? `${code}${formattedEduProgramCode}`
    : `${code}`;

  const directionTitle = educationProgram ? educationProgram.title : title;

  const targetOrgs =
    targetOrganizations && targetOrganizations.length > 0
      ? ` (${targetOrganizations.map(org => org.name).join(', ')})`
      : '';

  const fullDirectionTitle = `${directionCode} ${directionTitle} ${targetOrgs}`;

  const errorGroupTerms = {
    NOT_MATCH: 'Не соответствует наборам ВИ',
    NO_APPLICATION: 'Нет заявки на заключение договора о ЦО',
    NOT_CONFIRMED: 'Льгота не подтверждена',
  };

  const errorRequest = () => {
    const errors: string[] = [];
    if (admitted) return null;
    if (!entranceTestsSatisfied) errors.push(errorGroupTerms.NOT_MATCH);
    if (!targetContractPresent) errors.push(errorGroupTerms.NO_APPLICATION);
    if (!privilegeAdmitted) errors.push(errorGroupTerms.NOT_CONFIRMED);
    return errors.length ? errors : null;
  };

  const competitiveGroupTerms = {
    ...terms,
    REFUSED: 'Отклонено',
    REVOKED_BY_APPLICANT: 'Отозвано',
    COMPETITION_FAILED: 'Не прошло по конкурсу',
    ENROLLMENT_REFUSED: 'Отказ от зачисления',
    NEW: 'Новое',
    UNDER_CONSIDERATION: 'На рассмотрении',
    WAITING_EXAM: 'Ожидание результатов ВИ',
    ON_COMPETITION: 'Участвует в конкурсе',
    INCLUDED_IN_ENROLLMENT_ORDER: 'Включено в приказ на зачисление',
    ENROLLED: 'Зачислен',
  };

  return (
    <div>
      <div className="moderator-directions__badge">
        {epguStatus && (
          <Badge
            theme={
              epguStatus === CompetitiveGroupStatus.ENROLLED
                ? 'success'
                : epguStatus === CompetitiveGroupStatus.REFUSED ||
                  epguStatus === CompetitiveGroupStatus.COMPETITION_FAILED ||
                  epguStatus === CompetitiveGroupStatus.REVOKED_BY_APPLICANT ||
                  epguStatus === CompetitiveGroupStatus.ENROLLMENT_REFUSED
                ? 'danger'
                : 'info'
            }
            size="small"
          >
            {competitiveGroupTerms[epguStatus]}
          </Badge>
        )}

        {!admitted &&
          errorRequest()?.map(error => (
            <Badge key={error} theme="danger" size="small">
              {error}
            </Badge>
          ))}
      </div>

      <div
        className="moderator-directions-element"
        style={
          admitted ? {} : { border: '1px solid red', background: '#fff6f6' }
        }
      >
        <div
          className="moderator-directions-element__header"
          onClick={() => setOpen(!open)}
        >
          <h2>{fullDirectionTitle}</h2>
          <div className="moderator-directions-element__header-statuses">
            {asBenefit && <div>{terms.LGOT}</div>}

            {asQuota && <div>{terms.INDIVIDUAL_QUOTA}</div>}

            {!asMaster && !asBenefit && !asQuota && selectedPrivilege && (
              <div>{terms.WITHOUT_ENTRANCE_TESTS_SHORT}</div>
            )}

            {applicationEducationLevel ===
              DirectionApplicationEducationLevel.SECONDARY && (
              <div>{terms.GRADE_9}</div>
            )}

            {applicationEducationLevel ===
              DirectionApplicationEducationLevel.UPPER_SECONDARY && (
              <div>{terms.GRADE_11}</div>
            )}

            <div
              className={cn('moderator-directions-element__header-status', {
                'moderator-directions-element__header-status-deleted': true,
              })}
            >
              {deletedAt ? terms.REMOVED : null}
            </div>
          </div>
        </div>

        {open && (
          <div className="moderator-directions-element__body">
            <div className="moderator-data-list">
              <div className="moderator-data-list__element">
                <p>{terms.DISCIPLINES}:</p>
                <ul>
                  {entranceTestsSets
                    .map(sets => sets.subjects)
                    .flat(1)
                    .filter(
                      (value, index, array) =>
                        array.findIndex(v => v.id === value.id) === index
                    )
                    .map((test, index) => (
                      <li key={index}>
                        <span>{test.title}</span>
                        <br />
                      </li>
                    ))}
                </ul>
              </div>

              <div className="moderator-data-list__element">
                <p>{terms.SOURCE_OF_FINANCING}:</p>
                <span>
                  {isRussianLocale()
                    ? paymentForm.title
                    : paymentForm.titleForeign}
                </span>
              </div>
              {targetOrganizations && targetOrganizations.length > 0 && (
                <div className="moderator-data-list__element">
                  <p>{terms.TARGET_ORGANIZATIONS}:</p>
                  <ul>
                    {targetOrganizations.map((org, index) => (
                      <li key={index}>
                        <span>{org.name}</span>
                        <br />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="moderator-data-list__element">
                <p>Статус заявки на портале Работа в России:</p>
                <span>
                  {rtTargetAgreeStatus ? terms[rtTargetAgreeStatus] : '-'}
                </span>
              </div>

              <div className="moderator-data-list__element">
                <p>Дата создания заявки на портале Работа в России:</p>
                <span>
                  {rtTargetAgreeCreatedAt
                    ? formatDateTimeToRuFormat(rtTargetAgreeCreatedAt)
                    : '-'}
                </span>
              </div>

              {budgetLevel && (
                <div className="moderator-data-list__element">
                  <p>{terms.BUDGET_LEVEL}</p>
                  <span>
                    {isRussianLocale()
                      ? budgetLevel.title
                      : budgetLevel.titleForeign}
                  </span>
                </div>
              )}

              <div className="moderator-data-list__element">
                <p>{terms.EDUCATION_LANGUAGE}:</p>
                <span>
                  {isRussianLocale()
                    ? educationLanguage.title
                    : educationLanguage.titleForeign}
                </span>
              </div>

              <div className="moderator-data-list__element">
                <p>{terms.FORM_OF_STUDY}:</p>
                <span>{educationForm.title || '-'}</span>
              </div>

              <div className="moderator-data-list__element">
                <p>{terms.THE_LEVEL_OF_EDUCTION}:</p>
                <span>{educationLevel.title || '-'}</span>
              </div>

              <div className="moderator-data-list__element">
                <p>{terms.PRIORITY}:</p>
                <span>{priority || '-'}</span>
              </div>

              {educationProgram && (
                <div className="moderator-data-list__element">
                  <p>{terms.DIRECTION}: </p>
                  <span>{title}</span>
                </div>
              )}

              <div className="moderator-data-list__element">
                <p>{terms.SUBDIVISION}:</p>
                <span>{translate(faculty)}</span>
              </div>

              {epguStatus && (
                <div className="moderator-data-list__element">
                  <p>Cтатус КГ из ССПВО:</p>
                  <span>{competitiveGroupTerms[epguStatus]}</span>
                </div>
              )}

              {educationLevel.id === 4 && allegedEducationTrack && (
                <>
                  <b>Планирует поступить на:</b>
                  <div className="moderator-data-list__element">
                    <p>{terms.STUDY_FIELD}:</p>
                    <span>{allegedEducationTrack.desiredDirection || ''}</span>
                  </div>
                  <div className="moderator-data-list__element">
                    <p>{terms.THE_LEVEL_OF_EDUCTION}:</p>
                    <span>
                      {allegedEducationTrack.educationLevel.title || ''}
                    </span>
                  </div>
                </>
              )}

              <div className="moderator-data-list__element">
                <p>{terms.CREATION_DATE}:</p>
                <span>
                  {createdAt ? formatDateTimeToRuFormat(createdAt) : '-'}
                </span>
              </div>

              <div className="moderator-data-list__element">
                <p>{terms.DELETION_DATE}:</p>
                <span>
                  {deletedAt ? formatDateTimeToRuFormat(deletedAt) : '-'}
                </span>
              </div>

              <div className="moderator-data-list__element">
                <p>{terms.REFUSE_ENROLLMENT_DATE}:</p>
                <span>
                  {refuseEnrollmentDate
                    ? formatDateTimeToRuFormat(refuseEnrollmentDate)
                    : '-'}
                </span>
              </div>

              {currentApplication?.state === ApplicationState.ALLOWED && (
                <>
                  <div className="moderator-data-list__element">
                    <p>{terms.ADMITTED}:</p>
                    <span style={admitted ? {} : { color: 'red' }}>
                      {admitted ? terms.YES : terms.NO}
                    </span>
                  </div>

                  {!entranceTestsSatisfied && (
                    <div className="moderator-data-list__element">
                      <p>{terms.ENTRANCE_TESTS_SATISFIED}:</p>
                      <span>
                        {entranceTestsSatisfied ? terms.YES : terms.NO}
                      </span>
                    </div>
                  )}

                  {!privilegeAdmitted && (
                    <div className="moderator-data-list__element">
                      <p>{terms.PRIVILEGE_ADMITTED}:</p>
                      <span>{privilegeAdmitted ? terms.YES : terms.NO}</span>
                    </div>
                  )}

                  {!targetContractPresent && (
                    <div className="moderator-data-list__element">
                      <p>{terms.TARGET_CONTRACT_PRESENT}:</p>
                      <span>
                        {targetContractPresent ? terms.YES : terms.NO}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {educationLevel.title === 'СПО' && (
              <Controller
                name={fields.profileSubjectScore.name}
                control={control}
                render={({ field }) => (
                  <Input
                    {...inputV2Props(field)}
                    className="moderator-directions-element__body-input"
                    placeholder={terms.PROFILE_SCORE}
                  />
                )}
              />
            )}
            {currentApplication?.trajectory === Trajectory.COMMON && (
              <LocalPrivilegeForm
                asBenefit={!asMaster && asBenefit}
                asQuota={!asMaster && asQuota}
                asMaster={asMaster}
                withoutEntranceTests={
                  !asQuota &&
                  !asBenefit &&
                  currentApplication.educationLevel ===
                    ApplicationEducationLevel.BACHELOR
                }
                applicationDirectionId={applicationDirectionId}
                selectedPrivilegeId={selectedPrivilege?.id}
                applicationId={currentApplication?.id ?? 0}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeratorDirectionsElement;
